import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Input, DatePicker, Image, Row, Col, notification, Typography, Space } from 'antd';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import vn from 'antd/es/date-picker/locale/vi_VN';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import FormLabel from '../../../components/FormLabel';
import CustomSelect from '../../../components/CustomSelect';
import AudienceRuleEditor from '../../../components/AudienceRuleEditor';
import NormalFormLabel from '../../../components/NormalFormLabel';
import { LIST_STATUS_POPUP, LIST_BANNER_TYPE, LIST_PLATFORM, LIST_POPUP_CONTENT_TYPE } from '../../../constants/appSelect';
import { getBannerDetailRequest, createUpdateBannerRequest } from '../../../services/bannerRequest';
import { getListAudienceRequest } from '../../../services/audianceRequest';
import { LPD_banner, IMG_banner, MREC_banner } from '../../../assets/banner_image/banner_image';
import { useMutation, useQuery } from '@tanstack/react-query';
import { checkPermission } from '../../../utils/checkPermission';
import { CREATE_UPDATE_BANNER, GET_BANNER_DETAIL } from '../../../constants/apiEndPoint';
import { getListBannerPageRequest, getListVersionAndTopicRequest } from '../../../services/commonRequest';
dayjs.extend(buddhistEra);

const buddhistLocale = {
  ...vn,
  lang: {
    ...vn.lang,
    fieldDateFormat: 'YYYY-MM-DD',
    fieldDateTimeFormat: 'YYYY-MM-DD HH:mm',
    yearFormat: 'YYYY',
    cellYearFormat: 'YYYY',
  },
};

const SuaBanner = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Metadata đã lưu của bản ghi này theo từng loại nội dung — đổi loại thì hiện đúng metadata của loại đó
  const metadataByContentType = useRef({});
  const prevContentType = useRef();
  const handleChangeContentType = (value) => {
    const prev = prevContentType.current;
    metadataByContentType.current[prev] = form.getFieldValue('metadata');
    prevContentType.current = value;
    if (value === 'KHNLCN' || prev === 'KHNLCN' || metadataByContentType.current[value] !== undefined) {
      form.setFieldsValue({ metadata: metadataByContentType.current[value] });
    }
  };
  const { bannerId } = useParams();
  const watchBannerTypeValue = Form.useWatch('banner_type', form);
  const watchTypeTopicValue = Form.useWatch('type_topic', form);
  const customerGroupValue = Form.useWatch('audience', form);
  const outsideCustomerGroupValue = Form.useWatch('not_audience', form);
  const checkBirthdayValue = Form.useWatch('show_on_birthday', form); // Correctly monitor 'show_on_birthday'
  const contentValue = Form.useWatch('content_type', form);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const handleAddVersion = () => {
    const newValue = inputValue.trim();
    if (!newValue) return;

    const exists = options.some((option) => option.value === newValue);
    const newOptions = exists ? options : [...options, { label: newValue, value: newValue }];
    setOptions(newOptions);

    const currentValues = form.getFieldValue('version') || [];
    const newValues = Array.from(new Set([...currentValues, newValue]));
    form.setFieldsValue({ version: newValues });

    setInputValue('');
  };

  const backToPage = () => {
    navigate('/quan-ly-banner');
  };

  const getImageBanner = () => {
    if (contentValue === 'LDP') {
      return <Image width={200} src={LPD_banner} />;
    } else if (contentValue === 'Mrec') {
      return <Image width={200} src={MREC_banner} />;
    } else if (contentValue === 'Image') {
      return <Image width={200} src={IMG_banner} />;
    } else {
      return null;
    }
  };

  // get list audience
  const { data: audienceList } = useQuery({
    queryKey: ['audienceList'],
    queryFn: () => getListAudienceRequest(),
  });

  // convert list audience options
  const dataAudienceOptions = useMemo(() => {
    const options =
      audienceList?.map((item) => {
        return { label: item?.name, value: item?.code };
      }) || [];
    return options;
  }, [audienceList]);

  // get list version
  const { data: dataAllVersions } = useQuery({
    queryKey: ['dataAllVersions'],
    queryFn: () => getListVersionAndTopicRequest({ type: 2 }),
  });

  // convert list topic options
  useEffect(() => {
    const options =
      dataAllVersions?.map((item) => {
        return { label: item?.name, value: item?.value };
      }) || [];
    setOptions(options);
  }, [dataAllVersions]);

  // get list topic
  const { data: dataAllTopics } = useQuery({
    queryKey: ['dataAllTopics'],
    queryFn: () => getListVersionAndTopicRequest({ type: 3 }),
  });

  // convert list topic options
  const dataAllTopicOptions = useMemo(() => {
    const options =
      dataAllTopics?.map((item) => {
        return { label: item?.name, value: item?.value };
      }) || [];
    return options;
  }, [dataAllTopics]);

  // get data banners page
  const { data: dataAllBannerPage } = useQuery({
    queryKey: ['dataAllBannerPage'],
    queryFn: () => getListBannerPageRequest(),
  });

  // convert list banner page options
  const dataAllBannerPageOption = useMemo(() => {
    const options =
      dataAllBannerPage
        ?.filter((item) => !watchBannerTypeValue || item?.banner_type === watchBannerTypeValue)
        ?.map((item) => {
          return { label: item?.name, value: item?.id?.toString() };
        }) || [];
    return options;
  }, [dataAllBannerPage, watchBannerTypeValue]);

  const filteredOutsideCustomerGroupOptions = useMemo(() => {
    return dataAudienceOptions.filter((option) => !customerGroupValue?.includes(option.value));
  }, [customerGroupValue, dataAudienceOptions]);

  const filteredCustomerGroupOptions = useMemo(() => {
    return dataAudienceOptions.filter((option) => !outsideCustomerGroupValue?.includes(option.value));
  }, [outsideCustomerGroupValue, dataAudienceOptions]);

  const { data: bannerDetail } = useQuery({
    queryKey: ['bannerDetail', bannerId],
    queryFn: () => getBannerDetailRequest(bannerId),
  });

  useEffect(() => {
    if (bannerDetail) {
      metadataByContentType.current = { [bannerDetail?.content_type]: bannerDetail?.metadata };
      prevContentType.current = bannerDetail?.content_type;
      form.setFieldsValue({
        title: bannerDetail?.title, //Tiêu đề
        image: bannerDetail?.image, //Ảnh banner
        partner_url: bannerDetail?.partner_url, //Link
        place: bannerDetail?.place, //Thứ tự hiển thị
        page: bannerDetail?.page?.[0], //Vị trí hiển thị
        status: bannerDetail?.status, //Trạng thái
        description: bannerDetail?.description, //Mô tả

        metadata: bannerDetail?.metadata, //Metadata

        publish_time: bannerDetail?.publish_time ? dayjs(bannerDetail?.publish_time) : null, //Thời gian bắt đầu
        end_time: bannerDetail?.end_time ? dayjs(bannerDetail?.end_time) : null, //Thời gian kết thúc

        campaign_id: bannerDetail.campaign_id, //Chiến dịch
        partner_id: bannerDetail.partner_id,
        banner_type: bannerDetail?.banner_type, //Loại banner

        max_click: bannerDetail?.max_click, //Số click tối đa
        max_click_per_date: bannerDetail?.max_click_per_date, //Số click tối đa/ngày
        max_impression: bannerDetail?.max_impression, //Số impression tối đa
        max_impression_per_date: bannerDetail?.max_impression_per_date, //Số impression tối đa/ngày
        show_on_birthday: bannerDetail?.show_on_birthday || 0, //Kiểm tra hiển thị đúng vào ngày sinh nhật
        date_from_receive: bannerDetail?.date_from_receive, //Kể từ ngày nhận banner
        type_topic: bannerDetail?.type_topic, //Loại tập khách hàng
        platform: bannerDetail?.platform, //Nền tảng
        version: bannerDetail?.version, //Version
        topic: bannerDetail?.topic, //Topic
        audience: bannerDetail?.audience, //Tập khách hàng
        not_audience: bannerDetail?.not_audience, //Ngoài tập khách hàng
        audience_rule: bannerDetail?.audience_rule || null, //Quy tắc audience DNF
        content_type: bannerDetail?.content_type, // loại nội dung
      });
    }
  }, [bannerDetail, form]);

  const { mutate: mutateUpdateBanner, isPending: isLoadingUpdateBanner } = useMutation({
    mutationFn: createUpdateBannerRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Cập nhật banner thành công!',
        });
        backToPage();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error?.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      id: bannerDetail.id,
      group_id: bannerDetail.group_id, // id nhóm
      title: data?.title, //Tiêu đề
      image: data?.image, //Ảnh banner
      partner_url: data?.partner_url?.toString(), //Link
      place: data?.place, //Thứ tự hiển thị
      page: data?.page ? [data?.page] : [], //Vị trí hiển thị
      status: data?.status, //Trạng thái
      description: data?.description, //Mô tả

      metadata: data?.metadata, //Metadata

      publish_time: data?.publish_time, //Thời gian bắt đầu
      end_time: data?.end_time, //Thời gian kết thúc

      campaign_id: bannerDetail.campaign_id, //Chiến dịch
      partner_id: bannerDetail.partner_id,
      banner_type: data?.banner_type, //Loại banner

      max_click: data?.max_click, //Số click tối đa
      max_click_per_date: data?.max_click_per_date, //Số click tối đa/ngày
      max_impression: data?.max_impression, //Số impression tối đa
      max_impression_per_date: data?.max_impression_per_date, //Số impression tối đa/ngày
      show_on_birthday: data?.show_on_birthday, //Kiểm tra hiển thị đúng vào ngày sinh nhật
      date_from_receive: data?.date_from_receive, //Kể từ ngày nhận banner
      type_topic: data?.type_topic, //Loại tập khách hàng
      platform: data?.platform, //Nền tảng
      version: data?.version || [], //Version
      topic: data?.topic || [], //Topic
      audience: data?.audience || [], //Tập khách hàng
      not_audience: data?.not_audience || [], //Ngoài tập khách hàng
      audience_rule: data?.audience_rule || null, //Quy tắc audience DNF
      content_type: data?.content_type, // loại nội dung
    };
    mutateUpdateBanner(dataSubmit);
  };

  return (
    <div className="w-full">
      {checkPermission(GET_BANNER_DETAIL, 'Xem chi tiết') && checkPermission(CREATE_UPDATE_BANNER, 'Chỉnh sửa') && (
        <>
          <h1 className="font-semibold text-[24px] leading-[32px] gap-[10px] py-[10px]">Sửa banner</h1>

          <Form
            name="update-banner"
            layout="vertical"
            requiredMark={false}
            form={form}
            onFinish={handleSubmitForm}
            scrollToFirstError={{ behavior: 'smooth', block: 'center', focus: true }}
          >
            <Row gutter={[16, 0]}>
              <Col xs={12}>
                <Form.Item label={<FormLabel text="Chiến dịch quảng cáo" />} name="campaign_id">
                  <CustomSelect
                    placeholder="Chọn chiến dịch quảng cáo"
                    disabled
                    options={[{ value: bannerDetail?.campaign_id, label: bannerDetail?.campaignName }]}
                  />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Loại banner" />}
                  name="banner_type"
                  rules={[{ required: true, message: 'Vui lòng chọn loại banner' }]}
                >
                  <CustomSelect placeholder="Chọn loại banner" options={LIST_BANNER_TYPE} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={
                    <>
                      <FormLabel text="Vị trí hiển thị" />
                      <a
                        className="text-blue-400"
                        href="https://docs.google.com/spreadsheets/d/1YN7xizln-ajUjtE9Wn4uTv6GxrPwrTP-ohRk9zA9L4Y/edit?gid=1449111062#gid=1449111062"
                        target="_blank"
                        rel="noreferrer"
                      >
                        [Lịch Việt] List Banner quảng cáo
                      </a>
                    </>
                  }
                  rules={[{ required: true, message: 'Vui lòng chọn vị trí hiển thị' }]}
                  name="page"
                >
                  <CustomSelect
                    placeholder="Chọn vị trí hiển thị"
                    options={dataAllBannerPageOption}
                    disabled={bannerDetail?.page?.[0]}
                  />
                </Form.Item>
              </Col>

              {/* <Col xs={12}>
                <Form.Item label={<FormLabel text="Loại tập khách hàng" />} name="type_topic" initialValue={1}>
                  <CustomSelect
                    placeholder="Chọn loại tập khách hàng"
                    allowClear
                    options={[
                      { value: 1, label: 'Topic' },
                      { value: 2, label: 'Tập khách hàng' },
                    ]}
                  />
                </Form.Item>
              </Col> */}

              {/* {watchTypeTopicValue === 1 && (
                <> */}
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Nền tảng" />} name="platform">
                  <CustomSelect placeholder="Chọn nền tảng" allowClear options={LIST_PLATFORM} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item label={<NormalFormLabel text="Version" />} name="version">
                    <CustomSelect placeholder="Chọn version" allowClear mode="multiple" options={options} />
                  </Form.Item>
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onPressEnter={handleAddVersion}
                    placeholder="Nhập version mới"
                  />
                  <Button onClick={handleAddVersion}>Thêm version</Button>
                  <Typography.Text>
                    Nếu phiên bản có dạng v9.0:v9.3 thì sẽ hiển thị cho các phiên bản từ 9.0 đến 9.3. Nếu phiên bản có
                    dạng v9.0:999 thì sẽ hiển thị cho các phiên bản từ 9.0 trở lên. Nếu phiên bản có dạng v9.0 thì sẽ
                    hiển thị cho phiên bản 9.0. Nếu phiên bản có dạng 0:v9.0 thì sẽ hiển thị cho các phiên bản từ 9.0
                    trở xuống
                  </Typography.Text>
                </Space>
              </Col>
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Topic" />} name="topic">
                  <CustomSelect placeholder="Chọn topic" allowClear mode="multiple" options={dataAllTopicOptions} />
                </Form.Item>
              </Col>
              {/* </>
              )}

              {watchTypeTopicValue === 2 && (
                <> */}
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Tập khách hàng" />} name="audience">
                  <CustomSelect
                    placeholder="Chọn tập khách hàng"
                    allowClear
                    mode="multiple"
                    options={filteredCustomerGroupOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Quy tắc audience (AND/OR)" />} name="audience_rule">
                  <AudienceRuleEditor audienceOptions={dataAudienceOptions} />
                </Form.Item>
              </Col>
              {/* <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Ngoài tập khách hàng" />} name="not_audience">
                  <CustomSelect
                    placeholder="Chọn ngoài tập khách hàng"
                    allowClear
                    mode="multiple"
                    options={filteredOutsideCustomerGroupOptions}
                  />
                </Form.Item>
              </Col>
              </>
              )} */}

              <Col xs={24}>
                <Form.Item
                  label={<FormLabel text="Tiêu đề" />}
                  rules={[{ required: true, message: 'Nhập tiêu đề banner' }]}
                  name="title"
                >
                  <Input placeholder="Nhập tiêu đề banner..." />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Mô tả" />} name="description">
                  <Input placeholder="Nhập mô tả..." />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<FormLabel text="Link banner" />} name="partner_url">
                  <Input placeholder="Nhập link banner" disabled />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<FormLabel text="Ảnh banner" />} name="image">
                  <div className="gap-6">
                    <div>{bannerDetail?.image}</div>
                  </div>
                </Form.Item>
                <Image width={200} src={bannerDetail?.image} />
              </Col>

              <Col xs={12}>
                <Form.Item label={<FormLabel text="Thời gian bắt đầu" />} name="publish_time">
                  <DatePicker
                    format="HH:mm DD/MM/YYYY"
                    locale={buddhistLocale}
                    showTime
                    needConfirm={false}
                    disabled
                    style={{ width: '100%', height: '40px', backgroundColor: '#E0E2E7' }}
                  />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<FormLabel text="Thời gian kết thúc" />} name="end_time">
                  <DatePicker
                    format="HH:mm DD/MM/YYYY"
                    showTime
                    locale={buddhistLocale}
                    disabled
                    needConfirm={false}
                    style={{ width: '100%', height: '40px', backgroundColor: '#E0E2E7' }}
                  />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Loại nội dung" />}
                  rules={[{ required: true, message: 'Vui lòng chọn loại nội dung' }]}
                  name="content_type"
                >
                  <CustomSelect
                    placeholder="Chọn loại nội dung"
                    options={LIST_POPUP_CONTENT_TYPE}
                    onChange={handleChangeContentType}
                  />
                </Form.Item>
                {getImageBanner()}
              </Col>
              
              <Col xs={12}>
                {['LDP', 'Video', 'LDPNavigation', 'KHNLCN'].includes(contentValue) ? (
                  <Form.Item
                    label={<FormLabel text="Nội dung html (Metadata)" />}
                    rules={[
                      {
                        required: ['LDP', 'Video', 'LDPNavigation', 'KHNLCN'].includes(contentValue),
                        message: 'Vui lòng nhập nội dung html',
                      },
                    ]}
                    name="metadata"
                  >
                    <Input.TextArea rows={7} placeholder="Nhập nội dung html" />
                  </Form.Item>
                ) : (
                  <Form.Item label={<NormalFormLabel text="Metadata" />} name="metadata">
                    <Input placeholder="Nhập metadata" />
                  </Form.Item>
                )}
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số click tối đa" />} name="max_click">
                  <Input type="number" placeholder="Nhập số click tối đa..." />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số impression tối đa" />} name="max_impression">
                  <Input type="number" placeholder="Nhập số impression tối đa..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số click tối đa/ngày" />} name="max_click_per_date">
                  <Input type="number" placeholder="Nhập số click tối đa/ngày ..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Số impression tối đa/ngày" />} name="max_impression_per_date">
                  <Input type="number" placeholder="Nhập số impression tối đa/ngày ..." />
                </Form.Item>
              </Col>

              <Col xs={checkBirthdayValue === 1 ? 12 : 24}>
                <Form.Item
                  label={<FormLabel text="Kiểm tra hiển thị đúng vào ngày sinh nhật" />}
                  rules={[{ required: true, message: 'Chọn kiểm tra hiển thị đúng vào ngày sinh nhật' }]}
                  name="show_on_birthday"
                >
                  <CustomSelect
                    placeholder="Chọn kiểm tra hiển thị đúng vào ngày sinh nhật"
                    options={[
                      { value: 1, label: 'Kiểm tra' },
                      { value: 0, label: 'Không' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={checkBirthdayValue === 1 ? 12 : 0}>
                <Form.Item
                  label={<FormLabel text="Kể từ ngày nhận banner" />}
                  rules={[{ required: true, message: 'Nhập số ngày hiển thị banner kể từ ngày sinh nhật' }]}
                  name="date_from_receive"
                >
                  <Input type="number" placeholder="Nhập số ngày hiển thị banner kể từ ngày sinh nhật..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Thứ tự hiển thị" />}
                  rules={[{ required: true, message: 'Nhập thứ tự hiển thị' }]}
                  name="place"
                >
                  <Input min={1} type="number" placeholder="Nhập thứ tự hiển thị..." />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Trạng thái" />}
                  rules={[{ required: true, message: 'Chọn trạng thái' }]}
                  name="status"
                >
                  <CustomSelect placeholder="Chọn trạng thái" options={LIST_STATUS_POPUP} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <div className="flex justify-end gap-3">
                  <Form.Item>
                    <Button style={{ height: '40px' }} onClick={backToPage}>
                      <CloseOutlined /> Hủy bỏ
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button loading={isLoadingUpdateBanner} type="primary" htmlType="submit">
                      <SaveOutlined /> Lưu lại
                    </Button>
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

export default SuaBanner;
